import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Practitioner, Location } from '../types';
import PractitionerCard from '../components/PractitionerCard';
import SEO from '../components/SEO';
import { getCanonicalUrl } from '../utils/url';

interface PractitionerWithLocation extends Practitioner {
  locations: Location[];
}

export default function State() {
  const { state } = useParams<{ state: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [practitioners, setPractitioners] = useState<PractitionerWithLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);

  const formattedState = state?.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  // Group practitioners by city
  const cityGroups = useMemo(() => {
    const groups: Record<string, PractitionerWithLocation[]> = {};
    
    practitioners.forEach(practitioner => {
      practitioner.locations.forEach(location => {
        if (location.city) {
          if (!groups[location.city]) {
            groups[location.city] = [];
          }
          groups[location.city].push(practitioner);
        }
      });
    });
    
    return Object.entries(groups)
      .map(([city, practitioners]) => ({
        city,
        practitioners,
        count: practitioners.length
      }))
      .sort((a, b) => a.city.localeCompare(b.city));
  }, [practitioners]);

  // Get practitioners for the selected city
  const filteredPractitioners = useMemo(() => {
    if (!selectedCity) return [];
    return practitioners.filter(practitioner => 
      practitioner.locations.some(location => location.city === selectedCity)
    );
  }, [practitioners, selectedCity]);

  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: practitionersError } = await supabase
          .from('practitioners')
          .select(`
            *,
            locations!inner (*),
            specialties (
              id,
              name,
              description,
              created_at
            )
          `)
          .eq('locations.state', formattedState);

        if (practitionersError) throw practitionersError;
        setPractitioners(data || []);
      } catch (error) {
        console.error('Error fetching practitioners:', error);
        setError(error instanceof Error ? error.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    if (formattedState) {
      fetchPractitioners();
    }
  }, [formattedState]);

  // Set selected city from URL params on initial load
  useEffect(() => {
    const cityParam = searchParams.get('city');
    if (cityParam) {
      setSelectedCity(cityParam);
    } else {
      setSelectedCity(null);
    }
  }, [searchParams]);

  // Update URL when city selection changes
  const handleCitySelect = (city: string | null) => {
    setSelectedCity(city);
    if (city) {
      setSearchParams({ city });
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <SEO
        title={`Reiki Practitioners in ${formattedState} | Reiki Near Me`}
        description={`Find certified Reiki practitioners in ${formattedState}. Browse our directory of healers and book your session today.`}
        canonicalUrl={getCanonicalUrl(`/reiki/${state}${selectedCity ? `?city=${selectedCity}` : ''}`)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8">
          Reiki Practitioners in {formattedState}
        </h1>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Loading practitioners...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        ) : practitioners.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-300">
              No practitioners found in {formattedState}. Try searching in a different state or check back later.
            </p>
          </div>
        ) : (
          <>
            {/* City Filter */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                {selectedCity ? (
                  <div className="flex items-center">
                    <button 
                      onClick={() => handleCitySelect(null)}
                      className="mr-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                    >
                      ← Back to all cities
                    </button>
                    <span>Practitioners in {selectedCity}</span>
                  </div>
                ) : (
                  "Select a city"
                )}
              </h2>
              
              {!selectedCity && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {cityGroups.map(({ city, count }) => (
                    <button
                      key={city}
                      onClick={() => handleCitySelect(city)}
                      className="p-4 bg-white dark:bg-dark-secondary rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{city}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {count} {count === 1 ? 'practitioner' : 'practitioners'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Practitioners Grid */}
            {selectedCity && (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredPractitioners.map((practitioner) => (
                  practitioner.locations
                    .filter(location => location.city === selectedCity)
                    .map((location) => (
                      <PractitionerCard
                        key={`${practitioner.id}-${location.id}`}
                        practitioner={practitioner}
                        location={location}
                      />
                    ))
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}